import React from 'react';

// The 4 axes and their respective angles (in degrees).
// Arranged as a square: top, right, bottom, left.
const AXES = [
  { label: 'Estrés',       angle:   0 },   // top
  { label: 'Pasos',        angle:  90 },   // right
  { label: 'Consistencia', angle: 180 },   // bottom
  { label: 'Sueño',        angle: 270 },   // left
];

/**
 * Converts polar coordinates (angle, radius) to Cartesian (x, y).
 * Subtracts 90° so that angle 0 points upward (12 o'clock).
 */
function toXY(angleDeg, r, cx, cy) {
  const rad = (angleDeg - 90) * Math.PI / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

const RadarBienestar2 = ({ values = [] }) => {
  const cx = 140;
  const cy = 140;
  const maxR = 105; // Slightly reduced to leave room for labels on all 4 sides
  const levels = 4;

  // Normalize values (support decimals 0-1 and percentages 0-100)
  const normalizedValues = AXES.map((_, idx) => {
    const rawVal = values[idx] ?? 0;
    const normalized = rawVal > 1 ? rawVal / 100 : rawVal;
    return Math.max(0, Math.min(1, normalized));
  });

  // Calculate grid polygon points for each level
  const gridPolygons = Array.from({ length: levels }, (_, idx) => {
    const level = idx + 1;
    const r = (level / levels) * maxR;
    return AXES.map(ax => toXY(ax.angle, r, cx, cy));
  });

  // Calculate data polygon points
  const dataPoints = AXES.map((ax, idx) => {
    const value = normalizedValues[idx];
    return toXY(ax.angle, value * maxR, cx, cy);
  });
  const dataPointsStr = dataPoints.map(p => `${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(' ');

  return (
    <div className="w-full flex items-center justify-center">
      <svg
        width="100%"
        height="100%"
        viewBox="-10 10 300 260"
        className="w-full max-w-[300px] h-auto select-none"
        aria-label="Gráfico radar de bienestar"
        role="img"
      >
        {/* Grid Levels (Subtle Gray) */}
        {gridPolygons.map((points, idx) => {
          const pointsStr = points.map(p => `${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(' ');
          return (
            <polygon
              key={`grid-${idx}`}
              points={pointsStr}
              fill="none"
              stroke="rgba(0, 0, 0, 0.08)"
              strokeWidth="0.75"
            />
          );
        })}

        {/* Spokes (Subtle Gray) */}
        {AXES.map((ax, idx) => {
          const p = toXY(ax.angle, maxR, cx, cy);
          return (
            <line
              key={`spoke-${idx}`}
              x1={cx}
              y1={cy}
              x2={p[0].toFixed(2)}
              y2={p[1].toFixed(2)}
              stroke="rgba(0, 0, 0, 0.08)"
              strokeWidth="0.75"
            />
          );
        })}

        {/* Data Area Polygon (Brand Blue fill and stroke) */}
        <polygon
          points={dataPointsStr}
          fill="#0075AC"
          fillOpacity={0.15}
          stroke="#0075AC"
          strokeWidth="2"
          strokeLinejoin="round"
        />

        {/* Data Points (Dots in Brand Blue) */}
        {dataPoints.map((p, idx) => (
          <circle
            key={`dot-${idx}`}
            cx={p[0].toFixed(2)}
            cy={p[1].toFixed(2)}
            r="3.5"
            fill="#0075AC"
          />
        ))}

        {/* Axis Labels */}
        {AXES.map((ax, idx) => {
          const p = toXY(ax.angle, maxR + 18, cx, cy);
          return (
            <text
              key={`label-${idx}`}
              x={p[0].toFixed(2)}
              y={p[1].toFixed(2)}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#727272"
              className="text-[10px] font-medium tracking-wider fill-brand-gray-dark font-display"
              style={{
                fontFamily: "Satoshi, system-ui, -apple-system, sans-serif",
                fontSize: "10px",
              }}
            >
              {ax.label.toUpperCase()}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

export default RadarBienestar2;
